import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { LockupLogo, useTheme } from "@/design-system";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: `${API}/api`, withCredentials: true });

// 12.19d (Vania 1 Oct) — email sign-in only shows when the backend confirms
// RESEND_API_KEY is set. Prevents a dead end on live before the key is pasted.
// Google sign-in stays on its own config switch.

/**
 * Wave 11 (Vania · 1 Oct) · launch-blocking sign-in surface.
 *  · "continue with google" opens watu wako's OWN OAuth client (not the
 *    Emergent-managed one) — the Google consent screen now says watu wako.
 *  · "continue with email" asks for an email, posts /api/auth/email/request,
 *    collects the 6-digit code, posts /api/auth/email/verify, and lands the
 *    user on the same account they'd get from Google (match on email).
 *  · "continue with phone" is coming-soon (Africa's Talking waits on Vania).
 *  · "continue with telegram" removed per Vania's 1 Oct correction.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH. The Google redirect URL is always
 * `window.location.origin + "/auth/google"`.
 */
export default function SignInPrompt({
  title = "get in to continue",
  line = "get in to save events, follow curators and post.",
  showJoin = true,
  next,
  testId = "signin-prompt",
}) {
  const { pathname } = useLocation();
  const { theme } = useTheme();
  const logoGround = theme === "cream" ? "cream" : "night";
  const dest = next || pathname || "/app";
  const [emailMode, setEmailMode] = useState(null); // null | "email" | "code"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  // 12.19d — options surface tells us if email is live. Default closed so
  // we never flash a button we're about to hide.
  const [emailEnabled, setEmailEnabled] = useState(false);
  useEffect(() => {
    let dead = false;
    client.get("/auth/options")
      .then((r) => { if (!dead) setEmailEnabled(!!r.data?.email); })
      .catch(() => { if (!dead) setEmailEnabled(false); });
    return () => { dead = true; };
  }, []);

  const handleGoogle = () => {
    const clientId = window.__WATUWAKO_GOOGLE_CLIENT_ID__ ||
      (window.ENV && window.ENV.GOOGLE_OAUTH_CLIENT_ID) ||
      process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID || "";
    if (!clientId) {
      toast.error("google sign-in not configured yet — try email");
      return;
    }
    const redirect = window.location.origin + "/auth/google";
    const state = encodeURIComponent(dest);
    const url =
      "https://accounts.google.com/o/oauth2/v2/auth" +
      `?client_id=${encodeURIComponent(clientId)}` +
      `&redirect_uri=${encodeURIComponent(redirect)}` +
      "&response_type=id_token" +
      "&scope=openid%20email%20profile" +
      `&nonce=${Math.random().toString(36).slice(2)}` +
      `&state=${state}`;
    window.location.assign(url);
  };

  const requestCode = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      const r = await client.post("/auth/email/request", { email: email.trim() });
      setResendIn(r.data?.resend_in || 60);
      setEmailMode("code");
      toast.success("code sent — check your email");
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not send code");
    } finally { setBusy(false); }
  };

  const verifyCode = async () => {
    if (!/^\d{6}$/.test(code.trim())) {
      toast.error("the code is 6 digits");
      return;
    }
    setBusy(true);
    try {
      await client.post("/auth/email/verify", { email: email.trim(), code: code.trim() });
      toast.success("signed in");
      window.location.assign(dest);
    } catch (e) {
      toast.error(e.response?.data?.detail || "code didn't match");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={testId} className="flex flex-1 flex-col"
         style={{ background: "var(--ww-ground)", color: "var(--ww-text)" }}>
      <div className="flex-1 overflow-y-auto px-6 pt-10 pb-16">
        <div className="mx-auto text-center" style={{ maxWidth: 440 }}>
          <div className="mb-6 flex justify-center">
            <LockupLogo width={160} ground={logoGround} />
          </div>
          <h1 className="lowercase" style={{ fontFamily: "Poppins", fontWeight: 800,
                                            fontSize: 34, letterSpacing: "-0.025em",
                                            lineHeight: 1.05 }}>
            {title}
          </h1>
          <p className="mt-4" style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 15,
                                       lineHeight: 1.5 }}>
            {line}
          </p>

          {emailMode !== "code" && (
            <>
              <button onClick={handleGoogle} data-testid={`${testId}-google`}
                      style={yellowCta}>
                <GoogleIcon /> continue with google
              </button>
              {emailEnabled && (
                emailMode === "email" ? (
                  <div className="mt-3" style={{ textAlign: "left" }}>
                    <input data-testid={`${testId}-email-input`}
                           type="email" inputMode="email" autoComplete="email"
                           placeholder="you@example.com"
                           value={email} onChange={(e) => setEmail(e.target.value)}
                           style={field} />
                    <button onClick={requestCode} disabled={busy || !email.trim()}
                            data-testid={`${testId}-email-send`} style={{ ...ghostCta, marginTop: 10 }}>
                      {busy ? "sending…" : "send me a 6-digit code"}
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setEmailMode("email")}
                          data-testid={`${testId}-email-start`} style={ghostCta}>
                    continue with email
                  </button>
                )
              )}
              <button disabled style={{ ...ghostCta, opacity: 0.5, cursor: "not-allowed" }}
                      data-testid={`${testId}-phone-cs`}>
                continue with phone · coming soon
              </button>
            </>
          )}

          {emailMode === "code" && (
            <div className="mt-6" style={{ textAlign: "left" }}>
              <p style={{ fontFamily: "Arial", fontSize: 13, color: "var(--ww-text-muted)" }}>
                we sent a 6-digit code to <b style={{ color: "var(--ww-text)" }}>{email}</b>. it expires in 10 minutes.
              </p>
              <input data-testid={`${testId}-code-input`}
                     type="text" inputMode="numeric" autoComplete="one-time-code"
                     maxLength={6} placeholder="123456"
                     value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                     style={{ ...field, letterSpacing: "0.3em", textAlign: "center",
                              fontSize: 22, fontFamily: "Poppins", fontWeight: 700 }} />
              <button onClick={verifyCode} disabled={busy} data-testid={`${testId}-code-verify`}
                      style={{ ...yellowCta, marginTop: 10 }}>
                {busy ? "signing in…" : "sign in"}
              </button>
              <button onClick={() => { setEmailMode("email"); setCode(""); }}
                      style={{ ...ghostCta, marginTop: 10 }}>
                use a different email
              </button>
            </div>
          )}

          <p className="mt-4" data-testid="signin-legal-line"
             style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 12 }}>
            30 day free trial. we'll never charge you without asking first.<br/>
            by continuing you agree to our <a href="/terms" data-testid="signin-terms-link" style={legalLink}>terms</a> and <a href="/privacy" data-testid="signin-privacy-link" style={legalLink}>privacy policy</a>.
          </p>
          {showJoin && (
            <p className="mt-6" style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 13 }}>
              browsing is free. get in to save events and message your people. insiders follow, join squads and post.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

const yellowCta = {
  marginTop: 28, width: "100%", padding: "14px 20px", borderRadius: 999,
  background: "#FFD60A", color: "#1A1A30", border: 0,
  fontFamily: "Poppins", fontWeight: 700, fontSize: 15,
  textTransform: "lowercase", cursor: "pointer", minHeight: 48,
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10,
};
const ghostCta = {
  marginTop: 12, width: "100%", padding: "12px 20px", borderRadius: 999,
  background: "transparent", color: "var(--ww-text)",
  border: "1px solid var(--ww-line-strong)",
  fontFamily: "Poppins", fontWeight: 700, fontSize: 14,
  textTransform: "lowercase", cursor: "pointer", minHeight: 44,
};
const field = {
  width: "100%", padding: "12px 14px", borderRadius: 10,
  background: "var(--ww-sunken)", border: "1px solid var(--ww-line-strong)",
  color: "var(--ww-text)", fontFamily: "Arial", fontSize: 14, outline: "none",
};
const legalLink = { color: "var(--ww-text)", textDecoration: "underline" };

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}
